import { useFormik } from "formik";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Dropdown from "src/components/dropdowns/Dropdown";
import Input from "src/components/inputs/Input";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { PAYMENT_METHOD_OPTIONS } from "src/utils/constants";
import { number, object, string } from "yup";
import {
  useCreateExpense,
  useGetExpenseData,
  useUpdateExpense,
} from "../api-clients";

const FORM_FIELDS = {
  DATE: "date",
  CATEGORY: "category",
  AMOUNT: "amount",
  PAYMENT_MODE: "mode",
  REF_NUMBER: "refNumber",
  REMARKS: "remarks",
};

const CreateExpenses = ({
  isInViewMode = false,
}: {
  isInViewMode?: boolean;
}) => {
  useSetActiveTab(TAB_NAMES.EXPENSES);
  const navigate = useNavigate();
  const { expenseId: id } = useParams();

  const expenseId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!expenseId, [expenseId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetUserLoading, data } = useGetExpenseData(expenseId);

  const expenseData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateUser,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateExpense(expenseId);

  const { isLoading, isSuccess, execute, error } = useCreateExpense();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.DATE]: expenseData?.[FORM_FIELDS.DATE] ?? null,
      [FORM_FIELDS.CATEGORY]: expenseData?.[FORM_FIELDS.CATEGORY] ?? "",
      [FORM_FIELDS.AMOUNT]: expenseData?.[FORM_FIELDS.AMOUNT] ?? "",
      [FORM_FIELDS.PAYMENT_MODE]:
        expenseData?.[FORM_FIELDS.PAYMENT_MODE] ?? null,
      [FORM_FIELDS.REF_NUMBER]: expenseData?.[FORM_FIELDS.REF_NUMBER] ?? "",
      [FORM_FIELDS.REMARKS]: expenseData?.[FORM_FIELDS.REMARKS] ?? "",
    },
    validationSchema: object({
      [FORM_FIELDS.DATE]: string().required("This is a Required field."),
      [FORM_FIELDS.CATEGORY]: string().required("This is a Required field."),
      [FORM_FIELDS.AMOUNT]: number()
        .nullable()
        .required("This is a Required field."),
      [FORM_FIELDS.PAYMENT_MODE]: number().required(
        "This is a Required field."
      ),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateUser(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(
        isUpdateSuccess ? "Expense Updated" : "Expense Created."
      );
      navigate("/store/expenses");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (error || isUpdateError) {
      errorNotification();
    }
  }, [error, errorNotification, isUpdateError]);

  const isInLoading = useMemo(
    () => isLoading || isGetUserLoading || isUpdateLoading,
    [isGetUserLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <div className="flex flex-col gap-[20px] h-full">
      <TabHeader label="Add Expenses" showBreadcrumb />
      <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
          <DatePicker
            label="Date"
            value={values[FORM_FIELDS.DATE]}
            onChange={(value) => setFieldValue(FORM_FIELDS.DATE, value)}
            disabled={isFieldDisabled}
            error={getFieldError(FORM_FIELDS.DATE)}
            required
          />
          <Input
            label="Category"
            placeholder="Category"
            name={FORM_FIELDS.CATEGORY}
            value={values[FORM_FIELDS.CATEGORY] as string}
            error={getFieldError(FORM_FIELDS.CATEGORY)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.CATEGORY, e.target.value)
            }
            disabled={isFieldDisabled}
            required
          />

          <Input
            type="number"
            label="Amount"
            placeholder="Amount"
            name={FORM_FIELDS.AMOUNT}
            value={values[FORM_FIELDS.AMOUNT] as string}
            error={getFieldError(FORM_FIELDS.AMOUNT)}
            onChange={(e) => setFieldValue(FORM_FIELDS.AMOUNT, e.target.value)}
            disabled={isFieldDisabled}
            required
          />

          <Dropdown
            label="Payment Mode"
            placeholder="Select Mode"
            className={"w-full"}
            options={PAYMENT_METHOD_OPTIONS}
            value={values[FORM_FIELDS.PAYMENT_MODE]}
            onChange={(val) => setFieldValue(FORM_FIELDS.PAYMENT_MODE, val)}
            disabled={isFieldDisabled}
            error={getFieldError(FORM_FIELDS.PAYMENT_MODE)}
            required
          />

          <Input
            label="Payment ref number"
            placeholder="Payment ref number"
            name={FORM_FIELDS.REF_NUMBER}
            value={values[FORM_FIELDS.REF_NUMBER] as string}
            error={getFieldError(FORM_FIELDS.REF_NUMBER)}
            onChange={(e) =>
              setFieldValue(FORM_FIELDS.REF_NUMBER, e.target.value)
            }
            disabled={isFieldDisabled}
          />

          <Input
            label="Remarks"
            placeholder="Remarks"
            name={FORM_FIELDS.REMARKS}
            value={values[FORM_FIELDS.REMARKS] as string}
            error={getFieldError(FORM_FIELDS.REMARKS)}
            onChange={(e) => setFieldValue(FORM_FIELDS.REMARKS, e.target.value)}
            disabled={isFieldDisabled}
          />
        </div>
        <Button
          text={isLoading ? "Loading..." : "Submit"}
          onClick={handleSubmit}
          primary
          className="h-[40px] px-10"
          isDisabled={isFieldDisabled}
        />
      </div>
    </div>
  );
};

export default CreateExpenses;
